import { assertSessionMutationAllowed, resetSessionScope, scopedRequest, subscribeSessionScope } from './app/sessionScope.js';
import { createResourceVersionStore } from './app/resourceVersions.js';

function localApiBaseUrl() {
  if (typeof window === 'undefined') {
    return 'http://localhost:8000/api/v1';
  }

  const host = window.location.hostname || 'localhost';
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const isLoopback = ['localhost', '127.0.0.1', '::1'].includes(host);

  return isLoopback ? 'http://localhost:8000/api/v1' : `${protocol}//${host}:8000/api/v1`;
}

const EXPLICIT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '';
const API_BASE_URL = (EXPLICIT_API_BASE_URL || localApiBaseUrl()).replace(/\/$/, '');
const LOCAL_API_BASE_URL = 'http://localhost:8000/api/v1';
const FALLBACK_API_BASE_URL = !EXPLICIT_API_BASE_URL && API_BASE_URL !== LOCAL_API_BASE_URL
  ? LOCAL_API_BASE_URL
  : '';
const WEB_BASE_URL = API_BASE_URL.replace(/\/api\/v\d+$/, '');
const SESSION_HINT_KEY = 'byggo_company_session_v1';
const XSRF_COOKIE_NAME = 'XSRF-TOKEN-COMPANY';
const LEGACY_TOKEN_KEY = 'byggo_company_token';
const IMPERSONATION_KEY = 'byggo_company_impersonation';
const READ_METHODS = ['GET', 'HEAD', 'OPTIONS'];
const UNAVAILABLE_STATUSES = [502, 503, 504];
const resourceEtags = createResourceVersionStore();
const csrfRequests = new Map();
subscribeSessionScope(() => {
  resourceEtags.clear();
  csrfRequests.clear();
});

function apiRoot(baseUrl = API_BASE_URL) {
  return String(baseUrl || '').replace(/\/api\/v\d+\/?$/, '');
}

function cookieValue(name) {
  if (typeof document === 'undefined') return '';

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map((value) => value.trim())
    .find((value) => value.startsWith(prefix));

  if (!cookie) return '';

  try {
    return decodeURIComponent(cookie.slice(prefix.length));
  } catch {
    return cookie.slice(prefix.length);
  }
}

function csrfCookieValue() {
  return cookieValue(XSRF_COOKIE_NAME) || cookieValue('XSRF-TOKEN');
}

async function ensureCsrfCookie(baseUrl = API_BASE_URL, force = false) {
  if (typeof document === 'undefined') return '';

  const existingToken = csrfCookieValue();
  if (existingToken && !force) return existingToken;

  const root = apiRoot(baseUrl);
  if (!csrfRequests.has(root)) {
    const scope = scopedRequest();
    const request = fetch(`${root}/sanctum/csrf-cookie`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-Byggo-Client': 'company-web',
      },
      cache: 'no-store',
      signal: scope.signal,
    }).then((response) => {
      scope.assertCurrent();
      if (!response.ok) {
        throw createApiError(apiErrorMessage(response.status), {
          status: response.status,
          code: 'csrf_initialization_failed',
        });
      }

      return csrfCookieValue();
    }).finally(() => {
      scope.dispose();
      if (csrfRequests.get(root) === request) csrfRequests.delete(root);
    });

    csrfRequests.set(root, request);
  }

  return csrfRequests.get(root);
}

export function mutationKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `byggo-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

function createApiError(message, {
  status = 0,
  payload = null,
  code = '',
  title = '',
  fieldErrors = {},
  traceId = '',
  cause = null,
} = {}) {
  const error = new Error(message);
  error.status = status;
  error.payload = payload;
  error.code = code;
  error.title = title;
  error.fieldErrors = fieldErrors;
  error.traceId = traceId;
  if (cause) error.cause = cause;
  return error;
}

function apiErrorMessage(status, payload) {
  if (status === 0) {
    return 'Vi får inte kontakt med Byggo API just nu. Kontrollera anslutningen eller försök igen om en liten stund.';
  }

  if (UNAVAILABLE_STATUSES.includes(status)) {
    return 'Åtgärden kan inte slutföras just nu. Vänta en stund och försök igen.';
  }

  if (status === 419) {
    return 'Sessionen kunde inte verifieras. Ladda om sidan och logga in igen om det behövs.';
  }

  if (payload?.message) return payload.message;

  if (status >= 500) {
    return 'Servern svarade inte som väntat. Försök igen om en liten stund.';
  }

  return `API:t svarade med status ${status}. Försök igen.`;
}

export function getToken() {
  if (typeof localStorage === 'undefined') return '';
  return localStorage.getItem(SESSION_HINT_KEY) || '';
}

export function setToken(authenticated) {
  if (!authenticated) resetSessionScope('', true);
  if (typeof localStorage === 'undefined') return;

  // Detached web clients authenticate with the HttpOnly Laravel session cookie.
  // Local storage only contains a non-secret hint used by background refreshes.
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  if (authenticated) localStorage.setItem(SESSION_HINT_KEY, '1');
  else {
    localStorage.removeItem(SESSION_HINT_KEY);
    clearImpersonationSession();
  }
}

export function getImpersonationSession() {
  try {
    return JSON.parse(localStorage.getItem(IMPERSONATION_KEY) || 'null');
  } catch {
    return null;
  }
}

export function setImpersonationSession(session) {
  if (!session) {
    localStorage.removeItem(IMPERSONATION_KEY);
    return;
  }

  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session));
}

export function clearImpersonationSession() {
  localStorage.removeItem(IMPERSONATION_KEY);
}

export function consumeImpersonationTokenFromLocation() {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  const hashValue = url.hash.startsWith('#') ? url.hash.slice(1) : '';
  const hashParams = new URLSearchParams(hashValue);
  const token = url.searchParams.get('impersonation_token') || hashParams.get('impersonation_token');

  if (!token) return null;

  const readOnlyValue = url.searchParams.get('impersonation_read_only') || hashParams.get('impersonation_read_only') || '1';
  const session = {
    readOnly: ['1', 'true', 'yes'].includes(String(readOnlyValue).toLowerCase()),
    startedAt: new Date().toISOString(),
    source: 'admin',
  };

  url.searchParams.delete('impersonation_token');
  url.searchParams.delete('impersonation_read_only');
  hashParams.delete('impersonation_token');
  hashParams.delete('impersonation_read_only');
  url.hash = hashParams.toString();
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

  return { token, session };
}

function assertWritableRequest(path, options) {
  const method = String(options.method || 'GET').toUpperCase();
  const isLogout = String(path || '').includes('/auth/logout');
  const session = getImpersonationSession();

  if (!session?.readOnly || READ_METHODS.includes(method) || isLogout) return;

  const error = new Error('Read-only supportläge är aktivt. Avbryt impersoneringen för att kunna göra ändringar.');
  error.status = 423;
  error.payload = { message: error.message };
  throw error;
}

export function apiUrl(path, baseUrl = API_BASE_URL) {
  const value = String(path || '');
  if (value.startsWith('http')) return value;

  if (value.startsWith('/api/')) {
    return `${baseUrl.replace(/\/api\/v\d+$/, '')}${value}`;
  }

  return `${baseUrl}${value.startsWith('/') ? '' : '/'}${value}`;
}

export function webUrl(path = '') {
  const value = String(path || '');
  if (value.startsWith('http')) return value;

  return `${WEB_BASE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
}

export async function api(path, options = {}) {
  assertWritableRequest(path, options);
  const method = String(options.method || 'GET').toUpperCase();
  if (!READ_METHODS.includes(method)) assertSessionMutationAllowed(path);
  const scope = scopedRequest(options.signal, !READ_METHODS.includes(method));

  const headers = new Headers(options.headers || {});
  const requestKey = apiUrl(path, API_BASE_URL);
  const resourceKey = requestKey.split('?')[0];
  const versionRequest = resourceEtags.begin(resourceKey, !READ_METHODS.includes(method), requestKey);
  let versionResult = {};
  headers.set('Accept', 'application/json');
  headers.set('X-Byggo-Client', 'company-web');

  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (!READ_METHODS.includes(method) && !headers.has('Idempotency-Key')) {
    headers.set('Idempotency-Key', mutationKey());
  }

  if (['PATCH', 'PUT', 'DELETE'].includes(method) && !headers.has('If-Match') && versionRequest.etag) {
    headers.set('If-Match', versionRequest.etag);
  }

  async function request(baseUrl) {
    const isMutation = !READ_METHODS.includes(method);

    async function send(refreshCsrf = false) {
      if (isMutation) {
        const csrfToken = await ensureCsrfCookie(baseUrl, refreshCsrf);
        if (csrfToken) headers.set('X-XSRF-TOKEN', csrfToken);
      }

      scope.assertCurrent();

      try {
        const response = await fetch(apiUrl(path, baseUrl), {
          ...options,
          headers,
          credentials: 'include',
          signal: scope.signal,
          cache: options.cache || (READ_METHODS.includes(method) ? 'no-store' : 'default'),
        });
        scope.assertCurrent();
        return response;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        throw createApiError(apiErrorMessage(0), { status: 0, code: 'api_unreachable', cause: error });
      }
    }

    let response = await send();
    if (response.status === 419 && isMutation) {
      response = await send(true);
    }

    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json') ? await response.json().catch(() => ({})) : null;
    scope.assertCurrent();

    if (!response.ok) {
      if (response.status === 401) setToken('');
      const error = createApiError(apiErrorMessage(response.status, payload), {
        status: response.status,
        payload,
        code: payload?.code || (UNAVAILABLE_STATUSES.includes(response.status) ? 'api_unavailable' : ''),
        title: payload?.title || '',
        fieldErrors: payload?.field_errors || payload?.errors || {},
        traceId: payload?.trace_id || response.headers.get('x-trace-id') || '',
      });
      const retryAfter = response.headers.get('retry-after');
      error.retryAfterMs = retryAfter && /^\d+$/.test(retryAfter)
        ? Number(retryAfter) * 1000
        : Math.max(0, Date.parse(retryAfter || '') - Date.now()) || 0;
      throw error;
    }

    if (READ_METHODS.includes(method) && !resourceEtags.acceptsRead(versionRequest)) {
      // Discard the stale body as well as its ETag. Keeping a new ETag next to
      // old form data would permit the next save to overwrite our own changes.
      throw new DOMException('Ett äldre svar ersattes av en nyare ändring.', 'AbortError');
    }
    versionResult = { etag: response.headers.get('etag'), success: true, deleted: method === 'DELETE' };

    if (!READ_METHODS.includes(method) && !options.silentMutation && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('byggo:local-mutation', {
        detail: { method, path: String(path || '') },
      }));
    }

    return payload ?? response;
  }

  try {
    return await request(API_BASE_URL);
  } catch (error) {
    // A lost mutation response does not prove that no write happened. Never
    // replay it against a different API endpoint as a network fallback.
    if (!READ_METHODS.includes(method) || !FALLBACK_API_BASE_URL || error?.status || error?.name === 'AbortError') throw error;
    return await request(FALLBACK_API_BASE_URL);
  } finally {
    resourceEtags.finish(versionRequest, versionResult);
    scope.dispose();
  }
}

export async function fetchProtectedBlob(url) {
  const scope = scopedRequest();
  const headers = new Headers({ Accept: '*/*' });
  headers.set('X-Byggo-Client', 'company-web');

  async function request(baseUrl) {
    try {
      return await fetch(apiUrl(url, baseUrl), {
        headers,
        credentials: 'include',
        signal: scope.signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      throw createApiError(apiErrorMessage(0), { status: 0, code: 'api_unreachable', cause: error });
    }
  }

  let response;

  try {
    try {
      response = await request(API_BASE_URL);
    } catch (error) {
      if (!FALLBACK_API_BASE_URL || error?.name === 'AbortError') throw error;
      response = await request(FALLBACK_API_BASE_URL);
    }
    scope.assertCurrent();
    if (!response.ok) {
      const traceId = response.headers.get('x-trace-id') || '';
      throw createApiError(apiErrorMessage(response.status), {
        status: response.status,
        code: UNAVAILABLE_STATUSES.includes(response.status) ? 'api_unavailable' : '',
        traceId,
      });
    }

    const blob = await response.blob();
    scope.assertCurrent();
    const contentType = (response.headers.get('content-type') || blob.type || '').split(';')[0].trim().toLowerCase();
    const previewKind = contentType === 'application/pdf'
      ? ((await blob.slice(0, 5).text()) === '%PDF-' ? 'pdf' : 'download')
      : /^image\/(?:png|jpeg|gif|webp|bmp|avif|svg\+xml)$/.test(contentType)
        ? 'image'
        : ['text/plain', 'text/csv', 'application/json'].includes(contentType) ? 'text' : 'download';
    scope.assertCurrent();
    return {
      blob,
      objectUrl: URL.createObjectURL(blob),
      contentType,
      previewKind,
    };
  } finally {
    scope.dispose();
  }
}

export async function openProtectedUrl(url, options = {}) {
  const previewWindow = options.download ? null : window.open('', '_blank');

  if (previewWindow) {
    previewWindow.opener = null;
    previewWindow.document.title = 'Byggo';
    previewWindow.document.body.textContent = 'Laddar dokument...';
  }

  let objectUrl = '';

  try {
    const result = await fetchProtectedBlob(url);
    ({ objectUrl } = result);

    if (options.download || result.previewKind === 'download') {
      previewWindow?.close();
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = options.filename || 'byggo-fil';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      return;
    }

    if (!previewWindow) {
      URL.revokeObjectURL(objectUrl);
      throw createApiError('Webbläsaren blockerade förhandsvisningen. Tillåt popup-fönster för Byggo och försök igen.', {
        status: 0,
        code: 'preview_popup_blocked',
      });
    }

    if (result.previewKind === 'pdf') {
      previewWindow.location.replace(objectUrl);
    } else {
      // SVG is passive only in an image element. Text is inserted as text,
      // never navigated to or parsed as HTML in the authenticated origin.
      const element = previewWindow.document.createElement(result.previewKind === 'image' ? 'img' : 'pre');
      if (result.previewKind === 'image') {
        element.src = objectUrl;
        element.alt = options.filename || 'Dokument';
        element.style.maxWidth = '100%';
      } else {
        element.textContent = await result.blob.text();
        element.style.whiteSpace = 'pre-wrap';
      }
      previewWindow.document.body.replaceChildren(element);
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    if (previewWindow && !previewWindow.closed) previewWindow.close();
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

export async function startAccountingOAuth(provider) {
  if (!/^[a-z][a-z0-9_]*$/.test(String(provider || ''))) throw new Error('Ogiltigt ekonomisystem.');
  // Open during the click, before awaiting the request, so popup blockers do not
  // silently eat the handoff. No legacy login or arbitrary fallback URL exists.
  const popup = window.open('about:blank', '_blank');
  if (!popup) throw new Error('Tillåt den nya fliken i webbläsaren och försök igen.');
  popup.opener = null;
  try {
    const payload = await api(`/company/accounting-settings/oauth/${provider}`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    const target = new URL(payload?.url || '', WEB_BASE_URL);
    if (target.origin !== new URL(WEB_BASE_URL).origin
      || !new RegExp(`^/company/accounting/${provider}/authorize/[A-Za-z0-9]{64}$`).test(target.pathname)
      || target.search || target.hash || target.username || target.password) {
      throw new Error('Anslutningslänken kunde inte kontrolleras. Ingenting har öppnats hos leverantören.');
    }
    popup.location.replace(target.href);
  } catch (error) {
    popup.close();
    throw error;
  }
}

let pendingCompanyBridge = null;

export function consumeCompanyBridge(code) {
  // React StrictMode may replay the effect while the one-use exchange is still
  // pending. Share only the in-flight request; never cache an authenticated session.
  if (pendingCompanyBridge?.code === code) return pendingCompanyBridge.promise;
  const promise = exchangeCompanyBridge(code);
  pendingCompanyBridge = { code, promise };
  const clear = () => { if (pendingCompanyBridge?.promise === promise) pendingCompanyBridge = null; };
  promise.then(clear, clear);
  return promise;
}

async function exchangeCompanyBridge(code) {
  resetSessionScope();
  const payload = await api('/auth/company-web-bridge/consume', {
    method: 'POST',
    body: JSON.stringify({
      code,
      device_name: 'ByggoForetagWebb Bridge',
    }),
  });

  clearImpersonationSession();
  setToken(payload.authenticated || payload.auth_mode === 'session');
  return payload;
}

export async function login(email, password) {
  resetSessionScope();
  const payload = await api('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, device_name: 'ByggoForetagWebb', audience: 'company' }),
  });

  clearImpersonationSession();
  setToken(payload.authenticated || payload.auth_mode === 'session');
  return payload.user;
}

export async function requestPasswordReset(email) {
  return api('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(data) {
  return api('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function registerCompany(data) {
  resetSessionScope();
  const payload = await api('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ ...data, intent: 'company', device_name: 'ByggoForetagWebb' }),
  });

  clearImpersonationSession();
  setToken(payload.authenticated || payload.auth_mode === 'session');
  return payload.user;
}

export async function completeCompanyOnboarding(data) {
  return api('/auth/company-onboarding', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function logout() {
  await api('/auth/logout', { method: 'POST' });
  setToken('');
}
